import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import CustomModal from "../Commaon/CustomModal";
import ImageWithLoader from "../Commaon/ImageWithLoader";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { formatDateTime } from "../../utils/date";

const normalizeFiles = (files) => {
  if (!files) return [];
  return Array.isArray(files) ? files.filter(Boolean) : [files].filter(Boolean);
};

const isImageUrl = (url) => /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(String(url || ""));

const getFileName = (url, index) => {
  try {
    const pathName = new URL(url).pathname;
    return decodeURIComponent(pathName.split("/").pop() || `Prescription ${index + 1}`);
  } catch {
    return `Prescription ${index + 1}`;
  }
};

const PrescriptionsList = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewPrescription, setViewPrescription] = useState(null);
  const [notePrescription, setNotePrescription] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const getPrescriptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/all-prescriptions?page=${pageNo}&limit=${limit}`,
        { method: "GET" }
      );
      const data = response.data;
      if (data.s === 200) {
        setPrescriptions(data.r || []);
        setTotalRecords(data.tr || 0);
      } else {
        setPrescriptions([]);
        setTotalRecords(0);
      }
    } catch (error) {
      setPrescriptions([]);
      setTotalRecords(0);
      setSnack({
        notification: { message: "Error fetching prescriptions", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit]);

  useEffect(() => {
    getPrescriptions();
  }, [getPrescriptions]);

  const filteredPrescriptions = useMemo(() => {
    if (!query.trim()) return prescriptions;
    const search = query.toLowerCase();
    return prescriptions.filter((item) => {
      const user = item.userDetails || {};
      return (
        String(item._id || "").toLowerCase().includes(search) ||
        String(user.fullName || "").toLowerCase().includes(search) ||
        String(user.mobileNumber || "").toLowerCase().includes(search)
      );
    });
  }, [query, prescriptions]);

  const visibleIds = filteredPrescriptions.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const updatePrescription = async (prescription, changes, successMessage) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/prescriptions/${prescription._id}`, {
        method: "PUT",
        body: JSON.stringify(changes),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: successMessage, type: "success" },
        });
        getPrescriptions();
      } else {
        throw new Error(response?.data?.m || "Error updating prescription");
      }
    } catch (error) {
      setSnack({
        notification: {
          message: error.message || "Error updating prescription",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (prescription, status) =>
    updatePrescription(prescription, { status }, "Prescription status updated");

  const openNote = (prescription) => {
    setNotePrescription(prescription);
    setNoteValue(prescription.adminNote || "");
  };

  const saveNote = async (event) => {
    event.preventDefault();
    if (!notePrescription) return;
    await updatePrescription(notePrescription, { adminNote: noteValue }, "Prescription note saved");
    setNotePrescription(null);
  };

  const notePreview = (note) => {
    const text = String(note || "").trim();
    return text.length > 8 ? `${text.slice(0, 8)}...` : text;
  };

  const confirmBulkDelete = () => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one prescription", type: "error" },
      });
      return;
    }
    setDeleteId(null);
    setDeleteIds(selectedIds);
    setDeleteModel(true);
  };

  const deletePrescription = async () => {
    const ids = deleteIds.length ? deleteIds : deleteId ? [deleteId] : [];
    if (!ids.length) return;
    try {
      setLoading(true);
      const response = await AuthInterceptor(deleteIds.length ? "/admin/prescriptions" : `/admin/prescriptions/${deleteId}`, {
        method: "DELETE",
        body: deleteIds.length ? JSON.stringify({ ids }) : undefined,
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: {
            message: response?.data?.m || "Prescription deleted successfully",
            type: "success",
          },
        });
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        getPrescriptions();
      } else {
        throw new Error(response?.data?.m || "Error deleting prescription");
      }
    } catch (error) {
      setSnack({
        notification: { message: error.message || "Error deleting prescription", type: "error" },
      });
    } finally {
      setLoading(false);
      setDeleteId(null);
      setDeleteIds([]);
      setDeleteModel(false);
    }
  };

  const renderFiles = (files, compact = false) => {
    const items = normalizeFiles(files);
    if (!items.length) return <span className="text-muted">No files</span>;

    return (
      <div className={compact ? "d-flex align-items-center gap-2" : "d-grid gap-3"}>
        {items.slice(0, compact ? 2 : items.length).map((url, index) => (
          <div key={`${url}-${index}`} className={compact ? "d-flex align-items-center gap-2" : ""}>
            {isImageUrl(url) ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ImageWithLoader
                  src={url}
                  alt={`Prescription ${index + 1}`}
                  style={{
                    width: compact ? "44px" : "180px",
                    height: compact ? "44px" : "120px",
                    objectFit: "cover",
                  }}
                />
              </a>
            ) : (
              <a className="btn btn-sm btn-outline-secondary" href={url} target="_blank" rel="noopener noreferrer">
                View File {index + 1}
              </a>
            )}
            {!compact && (
              <div className="mt-2 d-flex flex-wrap gap-2">
                <a className="btn btn-sm btn-outline-custom" href={url} target="_blank" rel="noopener noreferrer">
                  Open
                </a>
                <a className="btn btn-sm btn-outline-secondary" href={url} download={getFileName(url, index)}>
                  Download
                </a>
              </div>
            )}
          </div>
        ))}
        {compact && items.length > 2 && <span className="text-muted small">+{items.length - 2}</span>}
      </div>
    );
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Prescriptions</h2>
          <select
            className="form-select form-select-sm w-auto"
            value={limit}
            onChange={(e) => {
              setPageNo(1);
              setLimit(Number(e.target.value));
            }}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>

        <div className="input-group mb-3">
          <input
            className="form-control"
            placeholder="Search prescriptions..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn-outline-secondary">Search</button>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">{selectedIds.length} selected</span>
          <button className="btn btn-sm btn-outline-danger" onClick={confirmBulkDelete}>
            Delete
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all prescriptions on this page"
                  />
                </th>
                <th>Sr.</th>
                <th>User</th>
                <th>Mobile</th>
                <th>Photos</th>
                <th>Upload Date</th>
                <th>Status</th>
                <th>Note</th>
                <th style={{ width: "160px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                filteredPrescriptions.map((item, index) => (
                  <tr key={item._id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(item._id)}
                        onChange={() => toggleRowSelection(item._id)}
                        aria-label={`Select prescription ${item._id}`}
                      />
                    </td>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td>{item.userDetails?.fullName || "-"}</td>
                    <td>{item.userDetails?.mobileNumber || "-"}</td>
                    <td>{renderFiles(item.fileUpload, true)}</td>
                    <td>{formatDateTime(item.createdAt)}</td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={item.status || "Open"}
                        onChange={(event) => handleStatusChange(item, event.target.value)}
                        aria-label={`Change status for prescription ${item._id}`}
                      >
                        <option value="Open">Open</option>
                        <option value="Processing">Processing</option>
                        <option value="Solved">Solved</option>
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-start p-0 text-decoration-none"
                        onClick={() => openNote(item)}
                        title={item.adminNote || "Add note"}
                      >
                        {item.adminNote ? notePreview(item.adminNote) : "Add note"}
                      </button>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-info" onClick={() => setViewPrescription(item)}>
                          <CgEye size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            setDeleteId(item._id);
                            setDeleteIds([]);
                            setDeleteModel(true);
                          }}
                        >
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && filteredPrescriptions.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No prescriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {totalPages}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.min(prev + 1, totalPages))}
            disabled={pageNo >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={Boolean(viewPrescription)} onHide={() => setViewPrescription(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Prescription Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewPrescription && (
            <div className="d-grid gap-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="text-muted small">Patient</div>
                  <div className="fw-semibold">{viewPrescription.userDetails?.fullName || "-"}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">Mobile</div>
                  <div className="fw-semibold">{viewPrescription.userDetails?.mobileNumber || "-"}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">Uploaded</div>
                  <div>{formatDateTime(viewPrescription.createdAt)}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">Status</div>
                  <div>{viewPrescription.status || "Open"}</div>
                </div>
              </div>
              <div>
                <div className="text-muted small">Admin Note</div>
                <div className="text-break">{viewPrescription.adminNote || "No note"}</div>
              </div>
              <div>
                <div className="text-muted small mb-2">Prescription Files</div>
                {renderFiles(viewPrescription.fileUpload)}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(notePrescription)} onHide={() => setNotePrescription(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Prescription Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={saveNote}>
            <div className="mb-3">
              <div className="text-muted small">Patient</div>
              <div className="fw-semibold">{notePrescription?.userDetails?.fullName || "-"}</div>
            </div>
            <div className="mb-3">
              <label className="form-label">Note</label>
              <textarea
                className="form-control"
                rows={5}
                value={noteValue}
                onChange={(event) => setNoteValue(event.target.value)}
                disabled={loading}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setNotePrescription(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Save note
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${deleteIds.length || 1} prescription${
          (deleteIds.length || 1) > 1 ? "s" : ""
        }?`}
        onConfirm={deletePrescription}
        onCancel={() => {
          setDeleteModel(false);
          setDeleteId(null);
          setDeleteIds([]);
        }}
      />
    </>
  );
};

export default PrescriptionsList;
